import { useCallback, useEffect, useState } from "react";
import type { AprendizajeEsperado, CriterioEvaluacion, ObjetivoGenerico } from "./types";

import {
  get_aprendizajes_esperados_tp,
  get_criterios_evaluacion_tp,
  get_objetivos_genericos_tp,
} from "../../services/api";

const extractArray = (respData: any): any[] => {
  if (Array.isArray(respData?.data)) return respData.data;
  if (Array.isArray(respData)) return respData;
  if (Array.isArray(respData?.items)) return respData.items;
  if (Array.isArray(respData?.data?.items)) return respData.data.items;
  return [];
};

const mapObjetivosGenericos = (arr: any[]): ObjetivoGenerico[] => {
  if (!Array.isArray(arr)) return [];
  return arr
    .map((og: any) => ({
      id: String(og.id_objetivo_generico ?? og.id ?? ""),
      texto: String(og.descripcion ?? og.texto ?? og.description ?? og.name ?? ""),
    }))
    .filter((x) => x.id);
};

const mapCriterios = (arr: any[]): CriterioEvaluacion[] => {
  if (!Array.isArray(arr)) return [];
  return arr
    .map((ce: any) => ({
      id: String(ce.id_criterio_evaluacion ?? ce.id ?? ""),
      texto: String(ce.descripcion ?? ce.texto ?? ce.description ?? ce.name ?? ""),
      objetivosGenericos: [],
    }))
    .filter((x) => x.id);
};

const mapAprendizajes = (arr: any[]): AprendizajeEsperado[] => {
  if (!Array.isArray(arr)) return [];
  return arr
    .map((ae: any) => ({
      id: String(ae.id_aprendizaje_esperado ?? ae.id ?? ""),
      texto: String(ae.descripcion ?? ae.texto ?? ae.description ?? ae.name ?? ""),
      criteriosEvaluacion: [],
    }))
    .filter((x) => x.id);
};

interface Params {
  open: boolean;
  objetivoId?: string | null;
}

export const useOATreeLoaderTP = ({ open, objetivoId }: Params) => {
  // Aprendizajes Esperados
  const [aprendizajes, setAprendizajes] = useState<AprendizajeEsperado[]>([]);
  const [loadingAE, setLoadingAE] = useState(false);
  const [errorAE, setErrorAE] = useState("");

  // Criterios de Evaluación por Aprendizajes Esperados
  const [criteriosByAE, setCriteriosByAE] = useState<Record<string, CriterioEvaluacion[]>>({});
  const [loadingCEByAE, setLoadingCEByAE] = useState<Record<string, boolean>>({});
  const [errorCEByAE, setErrorCEByAE] = useState<Record<string, string>>({});

  // Objetivos Genéricos por Criterio
  const [objetivosByCriterio, setObjetivosByCriterio] = useState<Record<string, ObjetivoGenerico[]>>(
    {}
  );
  const [loadingOGByCriterio, setLoadingOGByCriterio] = useState<Record<string, boolean>>({});
  const [errorOGByCriterio, setErrorOGByCriterio] = useState<Record<string, string>>({});

  // Estados para expandir acordeones
  const [expandedAE, setExpandedAE] = useState<Record<string, boolean>>({});
  const [expandedCE, setExpandedCE] = useState<Record<string, boolean>>({});

  // Limpieza para Aprendizajes esperados cuando objetivo se abre
  useEffect(() => {
    if (!open) return;

    // reset UI
    setExpandedAE({});
    setExpandedCE({});

    // reset caches
    setCriteriosByAE({});
    setLoadingCEByAE({});
    setErrorCEByAE({});
    setObjetivosByCriterio({});
    setLoadingOGByCriterio({});
    setErrorOGByCriterio({});

    // reset Aprendizaje Esperado
    setAprendizajes([]);
    setErrorAE("");

    if (!objetivoId) {
      setErrorAE("Objetivo inválido. No fue posible cargar aprendizajes esperados.");
      return;
    }

    const fetchAE = async () => {
      setLoadingAE(true);
      try {
        const resp = await get_aprendizajes_esperados_tp(objetivoId);
        if (!resp || resp.status !== 200) {
          console.error("get_aprendizajes_esperados_tp: respuesta inválida", resp);
          setErrorAE("No fue posible cargar los aprendizajes esperados.");
          setAprendizajes([]);
          return;
        }
        const raw = extractArray(resp.data);
        setAprendizajes(mapAprendizajes(raw));
      } catch (e) {
        console.error("Error consultando aprendizajes esperados TP:", e);
        setErrorAE("Ocurrió un error consultando aprendizajes esperados.");
        setAprendizajes([]);
      } finally {
        setLoadingAE(false);
      }
    };

    fetchAE();
  }, [open, objetivoId]);

  const fetchCEForAE = useCallback(
    async (aeId: string) => {
      if (!open) return;
      if (!aeId) return;
      if (criteriosByAE[aeId]) return;
      if (loadingCEByAE[aeId]) return;

      setLoadingCEByAE((prev) => ({ ...prev, [aeId]: true }));
      setErrorCEByAE((prev) => ({ ...prev, [aeId]: "" }));

      try {
        const resp = await get_criterios_evaluacion_tp(aeId);
        if (!resp || resp.status !== 200) {
          console.error("get_criterios_evaluacion_tp: respuesta inválida", resp);
          setErrorCEByAE((prev) => ({
            ...prev,
            [aeId]: "No fue posible cargar criterios para este aprendizaje.",
          }));
          setCriteriosByAE((prev) => ({ ...prev, [aeId]: [] }));
          return;
        }

        const raw = extractArray(resp.data);
        setCriteriosByAE((prev) => ({ ...prev, [aeId]: mapCriterios(raw) }));
      } catch (e) {
        console.error("Error consultando criterios TP:", e);
        setErrorCEByAE((prev) => ({ ...prev, [aeId]: "Ocurrió un error consultando criterios." }));
        setCriteriosByAE((prev) => ({ ...prev, [aeId]: [] }));
      } finally {
        setLoadingCEByAE((prev) => ({ ...prev, [aeId]: false }));
      }
    },
    [open, criteriosByAE, loadingCEByAE]
  );

  const fetchOGForCriterio = useCallback(
    async (criterioId: string) => {
      if (!open) return;
      if (!criterioId) return;
      if (objetivosByCriterio[criterioId]) return;
      if (loadingOGByCriterio[criterioId]) return;

      setLoadingOGByCriterio((prev) => ({ ...prev, [criterioId]: true }));
      setErrorOGByCriterio((prev) => ({ ...prev, [criterioId]: "" }));

      try {
        const resp = await get_objetivos_genericos_tp(criterioId);
        if (!resp || resp.status !== 200) {
          console.error("get_objetivos_genericos_tp: respuesta inválida", resp);
          setErrorOGByCriterio((prev) => ({
            ...prev,
            [criterioId]: "No fue posible cargar objetivos genéricos para este criterio.",
          }));
          setObjetivosByCriterio((prev) => ({ ...prev, [criterioId]: [] }));
          return;
        }

        const raw = extractArray(resp.data);
        setObjetivosByCriterio((prev) => ({ ...prev, [criterioId]: mapObjetivosGenericos(raw) }));
      } catch (e) {
        console.error("Error consultando objetivos genéricos TP:", e);
        setErrorOGByCriterio((prev) => ({
          ...prev,
          [criterioId]: "Ocurrió un error consultando objetivos genéricos.",
        }));
        setObjetivosByCriterio((prev) => ({ ...prev, [criterioId]: [] }));
      } finally {
        setLoadingOGByCriterio((prev) => ({ ...prev, [criterioId]: false }));
      }
    },
    [open, objetivosByCriterio, loadingOGByCriterio]
  );

  return {
    // Aprendizajes Esperados
    aprendizajes,
    loadingAE,
    errorAE,

    // Criterios de Evaluación cache
    criteriosByAE,
    loadingCEByAE,
    errorCEByAE,

    // Objetivos Genéricos cache
    objetivosByCriterio,
    loadingOGByCriterio,
    errorOGByCriterio,

    // expansions de los acordeones
    expandedAE,
    setExpandedAE,
    expandedCE,
    setExpandedCE,

    // fetch
    fetchCEForAE,
    fetchOGForCriterio,
  };
};
